// src/core/services/ErrorHandlerService.ts

import { IErrorHandler, ErrorInfo, ApiError } from '@core/interfaces/IErrorHandler.ts';
import { IAnalyticsService } from '@core/interfaces';
import { INotificationService } from '@core/interfaces';

/**
 * ErrorHandler Service Implementation
 * Handles errors throughout the application using injected dependencies
 */
export class ErrorHandlerService implements IErrorHandler {
    private initialized = false;

    constructor(
        private analyticsService: IAnalyticsService,
        private notificationService: INotificationService
    ) {
        console.log('🔷 ErrorHandlerService: Instance created with injected dependencies');
    }

    /**
     * Initialize global error handlers
     * Should be called once at application startup
     */
    public initialize(): void {
        if (this.initialized) {
            console.log('⚠️ ErrorHandlerService: Already initialized, skipping');
            return;
        }

        console.log('🔷 ErrorHandlerService: Initializing global error handlers...');
        this.setupGlobalErrorHandlers();
        this.initialized = true;
        console.log('✅ ErrorHandlerService: Global error handlers initialized');
    }

    private setupGlobalErrorHandlers(): void {
        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            console.error('Unhandled promise rejection:', event.reason);
            if (!this.isApiError(event.reason)) {
                // Track to analytics
                this.analyticsService.trackException(event.reason, {
                    errorType: 'UnhandledPromiseRejection',
                    pathname: window.location.pathname,
                    url: window.location.href,
                    timestamp: new Date().toISOString()
                });
            }

            // Handle the error
            this.handleError(event.reason, 'Unhandled Promise Rejection');
            event.preventDefault();
        });

        // Handle global JavaScript errors
        window.addEventListener('error', (event) => {
            console.error('Global error:', event.error);

            if (!this.isApiError(event.error)) {
                // Track to analytics
                this.analyticsService.trackException(event.error, {
                    errorType: 'GlobalError',
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                    pathname: window.location.pathname,
                    url: window.location.href,
                    timestamp: new Date().toISOString()
                });
            }

            // Handle the error
            this.handleError(event.error, 'Global Error');
            console.log('');
        });

        console.log('✅ ErrorHandlerService: Global handlers set up');
    }

    public handleError(error: any, context?: string): void {
        console.log('🔷 ErrorHandlerService: handleError called');
        console.error(`Error in ${context ?? 'Application'}:`, error);

        // Show appropriate notification
        if (this.isApiError(error)) {
            this.handleApiError(error);
        } else if (this.isNetworkError(error)) {
            this.handleNetworkError();
        } else {
            this.handleGenericError(error);
        }
    }

    public logError(error: any, context?: string, additionalInfo?: Record<string, any>): void {
        console.log('🔷 ErrorHandlerService: logError called (silent)');
        console.error(`Logged error in ${context ?? 'Application'}:`, error);

        // Only track to analytics, no notification
        this.analyticsService.trackException(error, {
            errorType: 'LoggedError',
            context: context,
            timestamp: new Date().toISOString(),
            ...additionalInfo
        });
    }

    public handleReactError(error: Error, errorInfo: ErrorInfo): void {
        console.log('🚨 ErrorHandlerService: React Error Boundary caught error');
        console.error('React error:', error, errorInfo);

        this.analyticsService.trackException(error, {
            errorType: 'ReactError',
            ...errorInfo,
            timestamp: new Date().toISOString()
        });

        this.notificationService.error(
            'Application Error',
            'A component error occurred. The page will be refreshed to recover.'
        );

        setTimeout(() => {
            window.location.reload();
        }, 3000);
    }

    public isInitialized(): boolean {
        return this.initialized;
    }

    private isApiError(error: any): error is ApiError {
        return error && typeof error === 'object' && 'status' in error;
    }

    private isNetworkError(error: any): boolean {
        return (
            error &&
            (error.code === 'NETWORK_ERROR' ||
                error.code === 'ERR_NETWORK' ||
                error.name === 'NetworkError' ||
                error.message?.includes('Network Error') ||
                error.message?.includes('Failed to fetch') ||
                !navigator.onLine)
        );
    }

    private handleApiError(error: ApiError): void {
        const status = error.status;
        let message = error.message ?? 'An API error occurred';

        if (error.properties) {
            const properties = Array.isArray(error.properties) ? error.properties.join(', ') : error.properties;
            message += ` (${properties})`;
        }

        switch (status) {
            case 400:
                this.notificationService.error('Bad Request', message);
                break;
            case 401:
                this.notificationService.error('Unauthorized', 'Your session has expired. Please log in again.');
                break;
            case 403:
                this.notificationService.error('Forbidden', 'You do not have permission to perform this action.');
                break;
            case 404:
                this.notificationService.error('Not Found', 'The requested resource was not found.');
                break;
            case 422:
                this.notificationService.warning('Validation Error', message);
                break;
            case 500:
            case 502:
            case 503:
            case 504:
                this.notificationService.error(
                    'Server Error',
                    'An internal server error occurred. Please try again later.'
                );
                break;
            default:
                this.notificationService.error('Error', message);
        }
    }

    private handleNetworkError(): void {
        this.notificationService.error(
            'Network Error',
            'Unable to connect to the server. Please check your internet connection and try again.'
        );
    }

    private handleGenericError(error: any): void {
        const message = error?.message ?? 'An unexpected error occurred';
        this.notificationService.error('Error', message);
    }
}
